/* eslint-disable @typescript-eslint/ban-types */
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { keyframes } from "@stitches/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "~/config/stitches";
import { ArrowAnimatedIndicator } from "~/design-system/ArrowAnimatedIndicator";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { ExtraButton } from "~/design-system/ButtonGroup";
import { Column, Columns } from "~/design-system/Columns";
import { useBreakpoint } from "~/design-system/hooks";
import { Icon } from "~/design-system/Icon";
import { Paragraph } from "~/design-system/Paragraph";
import { Text } from "~/design-system/Text";
import { createComponentHook } from "~/types";

type ActionBarPopupProps = {
  title?: string;
  itemsSelectedNumber?: number;
  actionMenu?: React.ReactNode;
  onClear?: () => void;
  onSelectAll?: () => void;
  collapsed?: boolean;
  collapsable?: boolean;
};

const show = keyframes({
  "0%": {
    opacity: 0,
    visibility: "hidden",
  },
  "100%": {
    opacity: 1,
    visibility: "visible",
  },
});

const StyledBox = styled(Box, {
  animation: `${show} 0.3s forwards`,
  transition: "border 0.3s ease-in-out, background-color 0.3s ease-in-out",
});

type ActionBarTitleProps = {
  title?: string;
  collapsable?: boolean;
  onClick: () => void;
  open: boolean;
};

function ActionBarTitle(props: ActionBarTitleProps) {
  const { t } = useTranslation();
  if (props.title || props.collapsable) {
    return (
      <Box
        paddingBottom={props.open ? "xsmall" : undefined}
        display="flex"
        flexDirection="row"
        width={"full"}
        justifyContent={"center"}
        alignItems="center"
        onClick={props.onClick}
        style={{ cursor: props.collapsable ? "pointer" : "default" }}
      >
        {props.title && (
          <Box textAlign="center">
            <Paragraph weight="strong" size="small" tone="white">
              {props.title}
            </Paragraph>
          </Box>
        )}
        {props.collapsable && (
          <Box paddingLeft="xxsmall">
            <Icon
              label={t("ActionBarPopup.openActions", "Open actions")}
              color={"white"}
            >
              {props.open ? <ChevronDownIcon /> : <ChevronUpIcon />}
            </Icon>
          </Box>
        )}
      </Box>
    );
  }
  return null;
}

type ActionBarSelectedItemsProps = {
  itemsSelectedNumber?: number;
  onClear?: () => void;
  onSelectAll?: () => void;
};

function ActionBarSelectedItems(props: ActionBarSelectedItemsProps) {
  const { t } = useTranslation();
  if (props.itemsSelectedNumber || props.onClear) {
    return (
      <Box
        display="flex"
        alignItems="center"
        width="full"
        marginRight="xsmall"
        gap="xsmall"
      >
        {props.itemsSelectedNumber && (
          <Text truncate size="xsmall" weight="medium" tone={"white"}>
            {t("ActionBarPopup.selectedItems", "{{number}} selected", {
              number: props.itemsSelectedNumber,
            })}
          </Text>
        )}
        {props.onSelectAll && (
          <Button
            onClick={() => props.onSelectAll?.()}
            variant="transparent"
            css={{
              "&:hover": {
                background: "none",
              },
              border: "1px solid white",
              color: "white",
              flexShrink: 0,
              fontSize: "12px",
              padding: "$xxsmall",
              textDecoration: "underline",
            }}
            size="xsmall"
            dataClovisActionId="selected-items-all"
          >
            {t("ActionBarPopup.selectAllButton", "Select all")}
          </Button>
        )}
        {props.onClear && (
          <Button
            onClick={() => props.onClear?.()}
            variant="transparent"
            css={{
              "&:hover": {
                background: "none",
              },
              color: "white",
              fontSize: "12px",
              padding: "$xxsmall",
              textDecoration: "underline",
            }}
            size="xsmall"
            dataClovisActionId="action-bar-popup-clear"
          >
            {t("ActionBarPopup.clearButton", "clear")}
          </Button>
        )}
      </Box>
    );
  }
  return null;
}

const useActionBarPopup = createComponentHook((props: ActionBarPopupProps) => {
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoint();
  const [open, setOpen] = React.useState(props.collapsed ? false : true);
  // If it have collapsed: true, it's automatically a collapsable component
  // If there is "collapsable" true, it's collapsable open by default
  const collapsable = Boolean(props.collapsable ?? props.collapsed);

  React.useEffect(() => {
    setOpen(props.collapsed ? false : true);
  }, [props.collapsed]);

  return {
    actions: {
      setOpen,
    },
    state: {
      breakpoint,
      collapsable,
      open,
    },
    t,
  };
});

function ActionBarPopup(props: ActionBarPopupProps) {
  const { actions, state } = useActionBarPopup(props);
  return (
    <ArrowAnimatedIndicator color="info">
      <StyledBox
        display="flex"
        alignItems="center"
        width="full"
        padding="xsmall"
        boxShadow="large"
        borderRadius="standard"
        background={"info"}
        justifyContent="spaceBetween"
        borderWidth="xsmall"
        css={{ color: "$gray600" }}
        flexDirection="column"
      >
        <ActionBarTitle
          title={props.title}
          collapsable={state.collapsable}
          open={state.open}
          // If it's collapsable, we open/close it on click
          onClick={() => state.collapsable && actions.setOpen(!state.open)}
        />
        {state.open && (
          <Columns
            alignY="center"
            align={props.itemsSelectedNumber ? "between" : "center"}
          >
            <Column width="content">
              {props.itemsSelectedNumber && (
                <ActionBarSelectedItems
                  itemsSelectedNumber={props.itemsSelectedNumber}
                  onClear={props.onClear}
                  onSelectAll={props.onSelectAll}
                />
              )}
            </Column>
            <Column width={state.breakpoint === "mobile" ? "fit" : "content"}>
              {props.actionMenu}
            </Column>
          </Columns>
        )}
      </StyledBox>
    </ArrowAnimatedIndicator>
  );
}

/* components usable in actionMenu */
const ActionMenuButton = styled(Button, {
  backgroundColor: "$white",
  fontSize: "12px !important",
});

const ActionMenuExtraButton = styled(ExtraButton, {
  backgroundColor: "$white",
  height: "100%",
});

export { ActionBarPopup, ActionMenuButton, ActionMenuExtraButton };
